/**
 * Royal Lady — network status banner.
 *
 * Slim top strip that appears when the browser reports `navigator.onLine === false`
 * OR when three consecutive API calls fail with a network error. It's the last
 * chance to give a customer clear feedback BEFORE the branded offline page
 * takes over the whole viewport (which only happens on a hard navigation).
 *
 * Design decisions:
 *   • Non-blocking — always positioned on top of the app; never covers CTAs.
 *   • Auto-dismisses when the connection returns.
 *   • Uses the same gold-on-black palette as the rest of the shell.
 *   • Never toasts — a toast would be lost as the user scrolls; the banner
 *     stays visible until it's no longer relevant.
 *   • Also handles the `beforeinstallprompt` event so we listen for the
 *     browser's install offer without ever nagging the user.
 *
 * Accessibility:
 *   • `role="status"` + `aria-live="polite"` so screen readers announce the
 *     change without interrupting the user's current focus.
 */
import { useEffect, useState, useCallback } from "react";
import { WifiOff, RefreshCw, Wifi } from "lucide-react";

const RESTORED_HIDE_MS = 3500;

export default function NetworkStatusBanner() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  // "just restored" briefly shows a positive confirmation before hiding.
  const [restored, setRestored] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setRestored(true);
      // Auto-hide the restored banner after a short celebration.
      const t = setTimeout(() => setRestored(false), RESTORED_HIDE_MS);
      return () => clearTimeout(t);
    };
    const handleOffline = () => {
      setOnline(false);
      setRestored(false);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const retry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 6000);
      const r = await fetch("/api/health?t=" + Date.now(), {
        method: "GET",
        cache: "no-store",
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (r && r.ok) {
        setOnline(true);
        setRestored(true);
        setTimeout(() => setRestored(false), RESTORED_HIDE_MS);
      }
    } catch (_) { /* still offline */ }
    setRetrying(false);
  }, [retrying]);

  if (online && !restored) return null;

  const isOffline = !online;
  return (
    <div
      data-testid="network-status-banner"
      role="status"
      aria-live="polite"
      className={
        "fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-3 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] shadow-md " +
        (isOffline
          ? "bg-black text-[#D4AF37] border-b border-[#D4AF37]/40"
          : "bg-[#0f2f1c] text-[#c8e8d0] border-b border-emerald-800/40")
      }
    >
      {isOffline ? (
        <>
          <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
          <span>You&apos;re offline — your cart is safe.</span>
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            data-testid="network-banner-retry"
            className="ml-2 inline-flex items-center gap-1.5 border border-[#D4AF37]/60 px-3 py-1 text-[10px] tracking-[0.2em] text-[#D4AF37] transition hover:bg-[#D4AF37] hover:text-black disabled:opacity-50"
          >
            <RefreshCw
              className={"h-3 w-3 " + (retrying ? "animate-spin" : "")}
              aria-hidden="true"
            />
            {retrying ? "Retrying" : "Retry"}
          </button>
        </>
      ) : (
        <>
          <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Connection restored — welcome back.</span>
        </>
      )}
    </div>
  );
}
